import React, { useState, useEffect } from "react";
import axios from "axios";
import { setGlobalVariable } from "../shared";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import './resultDetails.css';
import "./GridColorCodes.css";

function ResultDetail({ isOpen, onClose, assessmentResult, baseUrl, assessmentDetail }) {
  const [isExplaining, setIsExplaining] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [explanationError, setExplanationError] = useState(null);
  const [showExplanationModal, setShowExplanationModal] = useState(false);

  const handleExplain = async () => {
    setIsExplaining(true);
    setExplanationError(null);
    setExplanation(null);
    setShowExplanationModal(true);

    const token = sessionStorage.getItem("authToken");
    const studentkey = sessionStorage.getItem("studentkey");
    const districtID = sessionStorage.getItem("districtID");

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    try {
      const payload = {
        studentKey: studentkey,
        districtId: districtID,
        assessmentDetail: assessmentDetail,
        assessmentResult: assessmentResult,
        jsonMd5: assessmentDetail?.Json_MD5,
      };

      const response = await axios.post(
        baseUrl + 'Explain/GetExplanation',
        payload,
        { headers }
      );

      if (response.data) {
        setExplanation(response.data.explanation || response.data);
      }
    } catch (error) {
      console.error('Error getting explanation:', error);
      setExplanationError(error.response?.data?.message || 'Failed to get explanation. Please try again.');
    } finally {
      setIsExplaining(false);
    }
  };

  const closeExplanationModal = () => {
    setShowExplanationModal(false);
    setExplanation(null);
    setExplanationError(null);
  };

  const getQuestionColor = (assessmentResult, questionId) => {
    const question = assessmentResult.find(
      (item) => item.QuestionId === questionId
    );
    return question ? question["QuestionId!Color"] : "#ffffff";
  };

  const getClassForSEColor = (assessmentResult, questionId) => {
    const question = assessmentResult.find((item) => item.QuestionId === questionId);

    return question ? question["SE!Color"] : "default-class";
  };

  const getClassForDOKNumColor = (assessmentResult, questionId) => {
    const question = assessmentResult.find((item) => item.QuestionId === questionId);

    return question ? question["DOKNum!Color"] : "default-class";
  };

  const [columnDefs] = useState([
    {
      headerName: "Item", field: "QuestionId", minWidth: 80,
      cellStyle: (params) => ({
        textAlign: "center",
        alignItems: "center",
        color: "white",
        backgroundColor: getQuestionColor(assessmentResult, params.data.QuestionId),
      }),
      flex: 1, comparator: (num1, num2) => {
        return num1 - num2;
      },
    },

    { headerName: "Points", field: "Points", minWidth: 80, cellStyle: { textAlign: "center", alignItems: "center", }, flex: 1 },

    {
      headerName: "Content", field: "SE", minWidth: 90,
      cellClass: (params) => {
        const className = getClassForSEColor(assessmentResult, params.data.QuestionId);
        return className;
      },
      cellStyle: { textAlign: "center", alignItems: "center", }, flex: 1
    },

    {
      headerName: "Standard", field: "TEKSAsmtRelationType", minWidth: 100, flex: 2,
      cellClass: (params) => {
        const className = getClassForSEColor(assessmentResult, params.data.QuestionId);
        console.log("Cell Class:", className);
        return className;
      },
      cellStyle: { textAlign: "center", alignItems: "center", },
    },

    { headerName: "RC", field: "RCNum", minWidth: 80, cellStyle: { textAlign: "center", alignItems: "center", }, flex: 1 },
    
    {
      headerName: "RC Description", field: "RcDesc", minWidth: 250, flex: 5, cellStyle: {
        textAlign: "start", alignItems: "center"
      }, cellClass: "descWrap"
    },

    {
      headerName: "DOK", field: "DOKNum", minWidth: 100,
      cellClass: (params) => {
        const className = getClassForDOKNumColor(assessmentResult, params.data.QuestionId);
        console.log("Cell Class:", className);
        return className;
      },
      cellStyle: { textAlign: "center", alignItems: "center", }, flex: 2
    },

  ]);

  const gridOptions = {
    icons: {
      sortAscending: '<span class="ag-icon ag-icon-desc">▲</span>',
      sortDescending: '<span class="ag-icon ag-icon-asc">▼</span>',
    },
  };

  if (!isOpen) return null;

  return (
    <div>
      <div className="tab-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h5 style={{ margin: 0 }}>Result for the document: {assessmentDetail.Title}</h5>
          <button 
            className="explain-btn"
            onClick={handleExplain}
            disabled={isExplaining}
          >
            {isExplaining ? (
              <>
                <span className="explain-spinner"></span>
                Loading...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                </svg>
                Explain
              </>
            )}
          </button>
        </div>
        <div className="ag-theme-alpine" style={{ height: "400px", }}>
          <AgGridReact
            gridOptions={gridOptions}
            rowData={assessmentResult}
            columnDefs={columnDefs}
            suppressScrollOnNewData={true}
          />
        </div>
      </div>

      {/* Explanation Modal */}
      {showExplanationModal && (
        <div className="explanation-modal-overlay" onClick={closeExplanationModal}>
          <div className="explanation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="explanation-modal-header">
              <h3>Score Explanation: {assessmentDetail.Title}</h3>
              <button className="explanation-modal-close" onClick={closeExplanationModal}>×</button>
            </div>
            <div className="explanation-modal-body">
              {isExplaining && (
                <div className="explanation-loading">
                  <div className="explanation-spinner-large"></div>
                  <p>Generating personalized score explanation...</p>
                </div>
              )}
              {explanationError && (
                <div className="explanation-error">
                  <p>{explanationError}</p>
                </div>
              )}
              {explanation && !isExplaining && (
                <>
                  <div className="report-header">
                    <h3>AI Performance Analysis</h3>
                    <p>
                      <strong>Student:</strong> {sessionStorage.getItem("FullName") || ""}
                      {assessmentDetail.Title && <> | <strong>Assessment:</strong> {assessmentDetail.Title}</>}
                      {assessmentDetail.Subject && <> | <strong>Subject:</strong> {assessmentDetail.Subject}</>}
                      {assessmentDetail.Score && <> | <strong>Score:</strong> {assessmentDetail.Score}</>}
                      {assessmentDetail.ScaleScore && <> | <strong>Scale Score:</strong> {assessmentDetail.ScaleScore}</>}
                    </p>
                  </div>
                  <div className="explanation-content" dangerouslySetInnerHTML={{ __html: explanation }} />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResultDetail;
